import MarkEmailReadOutlinedIcon from '@mui/icons-material/MarkEmailReadOutlined';
import ForumOutlinedIcon from '@mui/icons-material/ForumOutlined';
import { PageHero } from '@/components/PageHero';
import { NotifyForm } from '@/components/NotifyForm';
import { Reveal } from '@/components/Reveal';
import { useSeo } from '@/seo/useSeo';

export function Contact(): JSX.Element {
  useSeo({
    title: 'Contact',
    description:
      'One email address, read by the actual team building Nexus — hello@nexusrmm.ai. No sales queue, no phone tree.',
    path: '/contact',
  });

  return (
    <div>
      <PageHero
        eyebrow="Contact"
        title="One inbox. No sales queue."
        subtitle="We're a small team, and hello@nexusrmm.ai reaches all of us directly — not a routing system that eventually finds a human."
      />

      <section className="pb-16 md:pb-24">
        <div className="container-content max-w-[720px]">
          <Reveal>
            <div className="flex items-start gap-4 rounded-2xl border border-white/10 bg-white/[0.02] p-6">
              <div className="flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-xl bg-nexus-purple/[0.14] text-nexus-purple-light">
                <MarkEmailReadOutlinedIcon fontSize="small" />
              </div>
              <div>
                <h2 className="font-display text-lg font-semibold text-nexus-ink">General &amp; sales</h2>
                <p className="mt-2 text-sm leading-relaxed text-white/70">
                  <a href="mailto:hello@nexusrmm.ai" className="text-nexus-purple-light no-underline hover:underline">
                    hello@nexusrmm.ai
                  </a>{' '}
                  — design-partner questions, general questions, anything about the product or the
                  roadmap.
                </p>
              </div>
            </div>
          </Reveal>

          <Reveal delay={80}>
            <div className="mt-5 flex items-start gap-4 rounded-2xl border border-white/10 bg-white/[0.02] p-6">
              <div className="flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-xl bg-nexus-purple/[0.14] text-nexus-purple-light">
                <ForumOutlinedIcon fontSize="small" />
              </div>
              <div>
                <h2 className="font-display text-lg font-semibold text-nexus-ink">
                  Security reports &amp; press
                </h2>
                <p className="mt-2 text-sm leading-relaxed text-white/70">
                  Same address, different subject line — put &quot;Security&quot; or &quot;Press&quot;
                  in the subject and it&apos;ll get to the right person on a small team fast. See the{' '}
                  <a href="/security" className="text-nexus-purple-light no-underline hover:underline">
                    trust center
                  </a>{' '}
                  for our vulnerability-reporting notes, or{' '}
                  <a href="/press" className="text-nexus-purple-light no-underline hover:underline">
                    press &amp; brand assets
                  </a>{' '}
                  for boilerplate and logos.
                </p>
              </div>
            </div>
          </Reveal>

          <Reveal delay={140}>
            <div className="mt-10 rounded-2xl border border-nexus-purple/25 bg-nexus-purple/[0.06] p-6 text-sm leading-relaxed text-white/70">
              What we don&apos;t have yet, honestly: a formal support SLA for anyone outside the
              design-partner cohort, or a phone line. We&apos;re a small team building in the open,
              and we&apos;d rather tell you that than imply an enterprise support desk that
              doesn&apos;t exist.
            </div>
          </Reveal>

          <Reveal delay={200}>
            <div className="mt-10 flex justify-center">
              <NotifyForm />
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
